import { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.scss';
import { Minus, Plus } from '@phosphor-icons/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { setQuickAddItem, showPreview, updateCart, updateDivergenceData, updateFinishesData } from '../../slices/app-slice';
import { updateHeader } from '../../slices/header-slice';
import { getDivergenceData, getFinishesData } from '../../api';
import { useDataLoading } from '../../hooks/useDataLoading';

import 'swiper/css';
import 'swiper/css/navigation';

import Animate from '../../components/animate';
import { Navigation } from 'swiper/modules';
import { useDispatch, useSelector } from 'react-redux';
import Item from '../../components/item';
import { formatPrice, matches } from '../../utility/utils';

import instagram from '../../assets/images/footer/instagram.svg';
import facebook from '../../assets/images/footer/facebook.svg';
import youtube from '../../assets/images/footer/youtube.svg';
import pinterest from '../../assets/images/footer/pinterest.svg';
import tiktok from '../../assets/images/footer/tiktok.svg';
import FeedbackModal from '../../components/feedback-modal';
import SEO from '../../components/seo';
import GalleryImage from '../../components/gallery-image';

import infoIcon from '../../assets/images/icons/info.svg';
import Tooltip from '../../components/tooltip';



const Divergence = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const announcement = useSelector((state) => state.app.homepage?.announcement);

    // State management
    const [quantity, setQuantity] = useState(1);
    const [showReadMore, setShowReadMore] = useState(false);
    const [selectedSpecies, setSelectedSpecies] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [selectedLiveEdge, setSelectedLiveEdge] = useState(null);
    const [selectedBaseFinish, setSelectedBaseFinish] = useState(null);
    const [selectedTopFinish, setSelectedTopFinish] = useState(null);
    const [showMagnify, setShowMagnify] = useState(false);
    const [recommendedProducts, setRecommendedProducts] = useState([]);
    const [divergence, setDivergence] = useState({});
    const [showSeating, setShowSeating] = useState(false);
    const [totalPrice, setTotalPrice] = useState(0);
    const [currentImages, setCurrentImages] = useState([]);
    const [finishOption, setFinishOption] = useState("");
    const [isLoadingImages, setIsLoadingImages] = useState(true);
    const [loadedImages, setLoadedImages] = useState(0);
    const [title, setTitle] = useState("");
    const [showFinishes, setShowFinishes] = useState(false);

    const divergenceData = useSelector((state) => state.app.divergence);
    const finishesData = useSelector((state) => state.app.finishes);

    const [isDataLoading, setIsDataLoading] = useState(false);
    const hasFetched = useRef(false);

    useDataLoading('divergence', isDataLoading, []);

    useEffect(() => {
        const fetchData = async () => {
            const hasDivergenceData = divergenceData && divergenceData.divergenceMetadata;
            const hasFinishesData = finishesData && Array.isArray(finishesData) && finishesData.length > 0;

            if ((!hasDivergenceData || !hasFinishesData) && !hasFetched.current) {
                hasFetched.current = true;
                setIsDataLoading(true);
                try {
                    const promises = [];
                    
                    if (!hasDivergenceData) {
                        promises.push(getDivergenceData());
                    }
                    
                    if (!hasFinishesData) {
                        promises.push(getFinishesData());
                    }

                    const responses = await Promise.all(promises);
                    
                    let responseIndex = 0;
                    if (!hasDivergenceData) {
                        dispatch(updateDivergenceData(responses[responseIndex].data));
                        responseIndex++;
                    }
                    
                    if (!hasFinishesData) {
                        dispatch(updateFinishesData(responses[responseIndex].data));
                    }
                } catch (error) {
                    console.error('Error fetching divergence or finishes data:', error);
                } finally {
                    setIsDataLoading(false);
                }
            }
        };

        fetchData();
    }, [divergenceData, finishesData, dispatch]);

    useEffect(() => {
        if (divergenceData) {
            setDivergence(divergenceData);
            setShowMagnify(true);

            if (divergenceData.generalImagery && divergenceData.generalImagery.length > 0) {
                setCurrentImages(divergenceData.generalImagery);
            }
        }
    }, [divergenceData]);

    const handleImageLoad = useCallback((imageIndex) => {
        setLoadedImages(prev => {
            const newCount = prev + 1;
            if (newCount >= currentImages.length && currentImages.length > 0) {
                setTimeout(() => setIsLoadingImages(false), 200);
            }
            return newCount;
        });
    }, [currentImages.length]);

    const handleImageError = useCallback((imageIndex) => {
        setLoadedImages(prev => {
            const newCount = prev + 1;
            if (newCount >= currentImages.length && currentImages.length > 0) {
                setTimeout(() => setIsLoadingImages(false), 200);
            }
            return newCount;
        });
    }, [currentImages.length]);

    useEffect(() => {
        if (currentImages.length === 0) {
            setIsLoadingImages(false);
            setLoadedImages(0);
        } else {
            setIsLoadingImages(true);
            setLoadedImages(0);

            // Fallback timeout - if images don't load in 5 seconds, hide loader
            const timeout = setTimeout(() => {
                setIsLoadingImages(false);
                setLoadedImages(0);
            }, 5000);

            return () => clearTimeout(timeout);
        }
    }, [currentImages]);

    const handleSpeciesChange = (speciesId) => {
        if (!divergence?.variantSpecies || !speciesId) return; 
        
        const species = divergence.variantSpecies.find(s => s?.id === parseInt(speciesId));
        if (!species) return; 
        
        setSelectedSpecies(species);
        setSelectedSize(null);
        setSelectedLiveEdge(null);
        setSelectedBaseFinish(null);
        setSelectedTopFinish(null);
        setTitle(species?.name + " Dining Table");

        if (species?.speciesGeneralImagery && species.speciesGeneralImagery.length > 0) {
            setCurrentImages(species.speciesGeneralImagery);
        } else if (divergence?.generalImagery && divergence.generalImagery.length > 0) {
            setCurrentImages(divergence.generalImagery);
        } else {
            const firstFinish = species?.speciesFinishes?.[0];
            if (firstFinish?.images && firstFinish.images.length > 0) {
                setCurrentImages(firstFinish.images);
            } else {
                setCurrentImages([]);
                setIsLoadingImages(false);
            }
        }
    };

    const handleSizeChange = (sizeId) => {
        if (!selectedSpecies || !sizeId) return;

        const size = selectedSpecies.metadata?.find(m => m?.id === parseInt(sizeId));
        if (!size) return;
        
        setSelectedSize(size);
        setSelectedLiveEdge(null);
        setSelectedBaseFinish(null);
        setSelectedTopFinish(null);
        setTitle(selectedSpecies?.name + " Dining Table - " + size?.size);

        // Reset images to species images when size changes
        if (selectedSpecies?.speciesGeneralImagery && selectedSpecies.speciesGeneralImagery.length > 0) {
            setCurrentImages(selectedSpecies.speciesGeneralImagery);
        } else if (divergence?.generalImagery && divergence.generalImagery.length > 0) {
            setCurrentImages(divergence.generalImagery);
        } else {
            const firstFinish = selectedSpecies?.speciesFinishes?.[0];
            if (firstFinish?.images && firstFinish.images.length > 0) {
                setCurrentImages(firstFinish.images);
            } else {
                setCurrentImages([]);
                setIsLoadingImages(false);
            }
        }
    };

    const calculateTotalPrice = useCallback(() => {
        if (!selectedSize?.price) {
            setTotalPrice(0);
            return;
        }

        let price = selectedSize.price || 0;
        const liveEdgePrice = selectedLiveEdge === 'yes' ? (divergence?.divergenceMetadata?.liveEdgePrice || 0) : 0;
        const ceramicCoatingPrice = selectedTopFinish === 'Ceramic Coating' ? (divergence?.divergenceMetadata?.ceramicCoatingPrice || 0) : 0;

        if (selectedLiveEdge === 'yes') {
            price += liveEdgePrice;
        }

        if (selectedTopFinish === 'Ceramic Coating') {
            price += ceramicCoatingPrice;
        }

        setTotalPrice(price);
    }, [selectedSize, selectedLiveEdge, selectedTopFinish, divergence?.divergenceMetadata]);

    useEffect(() => {
        calculateTotalPrice();
    }, [calculateTotalPrice]);

    const handleLiveEdgeChange = (value) => {
        setSelectedLiveEdge(value);
        setSelectedBaseFinish(null);
        setSelectedTopFinish(null);
    };

    const handleBaseFinishClick = (finish) => {
        if (!finish) return;
        
        setFinishOption(finish);
        setSelectedBaseFinish(finish);
        setSelectedTopFinish(null);

        if (selectedSpecies && finish) {
            const finishVariant = selectedSpecies.speciesFinishes?.find(finishVariant =>
                matches(selectedSpecies?.name, finishVariant?.name) &&
                matches(finish, finishVariant?.name)
            );

            setTitle(finishVariant?.name + " Dining Table - " + selectedSize?.size);

            if (finishVariant?.images && finishVariant?.images?.length > 0) {
                setCurrentImages(finishVariant?.images);
            } else {
                // Fallback to species general images or first finish variant
                if (selectedSpecies.speciesGeneralImagery?.length > 0) {
                    setCurrentImages(selectedSpecies.speciesGeneralImagery);
                } else {
                    const firstFinish = selectedSpecies.speciesFinishes?.[0];
                    if (firstFinish?.images?.length > 0) {
                        setCurrentImages(firstFinish.images);
                    } else {
                        setIsLoadingImages(false);
                    }
                }
            }
        }
    };

    const handleTopFinishChange = (finish) => {
        setSelectedTopFinish(finish);
    };

    const getCurrentFolder = () => {
        if (selectedBaseFinish) {
            return "divergenceSpeciesFinish";
        } else if (selectedSpecies) {
            return "divergenceSpecies";
        } else {
            return "divergence";
        }
    };

    const addToCart = useCallback(() => {
        if (!selectedSpecies || !selectedSize || !divergence?.divergenceMetadata) return; // Safety check

        const basePrice = selectedSize.price || 0;
        const liveEdgePrice = selectedLiveEdge === 'yes' ? (divergence.divergenceMetadata?.liveEdgePrice || 0) : 0;
        const ceramicCoatingPrice = selectedTopFinish === 'Ceramic Coating' ? (divergence.divergenceMetadata?.ceramicCoatingPrice || 0) : 0;

        const productData = {
            id: divergence.divergenceMetadata?.divergenceId,
            title: title || "Divergence Dining Table",
            species: selectedSpecies?.name || "",
            size: selectedSize?.size || "",
            liveEdge: selectedLiveEdge,
            baseFinish: selectedBaseFinish,
            topFinish: selectedTopFinish,
            price: totalPrice || 0,
            pricingBreakdown: {
                basePrice: basePrice,
                liveEdgePrice: liveEdgePrice,
                ceramicCoatingPrice: ceramicCoatingPrice,
                totalPrice: totalPrice || 0
            },
            quantity: quantity || 1,
            images: currentImages || [],
            type: "Divergence",
            customShipping: true
        };

        dispatch(updateCart({
            product: productData,
            quantity: quantity,
            total: totalPrice * quantity
        }));

        dispatch(setQuickAddItem({
            product: productData,
            quantity: 1,
            total: totalPrice
        }));

        dispatch(showPreview(true));
    }, [dispatch, divergence, selectedSpecies, selectedSize, selectedLiveEdge, selectedBaseFinish, selectedTopFinish, quantity, totalPrice, currentImages, title]);

    useEffect(() => {
        dispatch(updateHeader('light'));
    }, [dispatch]);

    const handleQuantityClick = useCallback((val) => {
        if (selectedSize?.stock) {
            if (val <= selectedSize.stock) {
                setQuantity(val < 0 ? 0 : val);
            } else {
                setQuantity(selectedSize.stock);
            }
        } else {
            setQuantity(val < 0 ? 0 : val);
        }
    }, [selectedSize]);

    const onProductClick = (item) => {
        if (!item?.id || !item?.title) return;
        
        if (item.type === "DiningTable") {
            navigate({
                pathname: `/divergence/${item.id}`,
                search: `?title=${item.title}`,
            });
        } else {
            navigate({
                pathname: `/products/${item.id}`,
                search: `?title=${item.title}`,
            });
        }
        window.scrollTo(0, 0);
    };

    const onFeedbackClose = () => {
        setShowReadMore(false);
    };

    return (
        <>
            {divergence && divergence.divergenceMetadata &&
                <>
                    <SEO
                        title={divergence.divergenceMetadata.title || "Paramount Wood Co."}
                        description={divergence.divergenceMetadata.description ? divergence.divergenceMetadata.description.replace(/<[^>]*>/g, '') : ""}
                        name="Paramount Wood Co."
                        type="website"
                        url={window.location.href}
                    />
                    <div className={`divergence-page ${announcement ? 'has-announcement' : ''}`}>
                        <Animate>
                            <div className='divergence'>
                                <div className="divergence-gallery">
                                    <div className='divergence-gallery-images'>

                                        {isLoadingImages && (
                                            <div className="image-loader">
                                                <div className='loader'></div>
                                            </div>
                                        )}
                                        {currentImages?.length > 0 &&
                                            <Swiper
                                                slidesPerView={1}
                                                modules={[Navigation]}
                                                navigation={true}
                                                loop
                                                className={isLoadingImages ? 'loading' : ''}
                                            >
                                                {currentImages.map((img, i) => (
                                                    <SwiperSlide key={i}>
                                                        <GalleryImage
                                                            src={`${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/${getCurrentFolder()}/${img.fileName}&width=1500&height=1500&quality=99&format=webp`}
                                                            index={i}
                                                            onLoad={handleImageLoad}
                                                            onError={handleImageError}
                                                        />
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        }
                                    </div>
                                </div>

                                <div className="divergence-info">

                                    {title
                                        ? <h1>{title}</h1>
                                        : <h1>{divergence.divergenceMetadata?.title}</h1>
                                    }

                                    <div className='price-wrapper'>
                                        {selectedSize ? (
                                            <p className='divergence-price'>{formatPrice(totalPrice)} (Sculpted T Base included)</p>
                                        ) : (
                                            <p className='divergence-price'>Follow the steps below to get a price</p>
                                        )}
                                    </div>

                                    {selectedSize && (
                                        <div className='divergence-quantity-wrapper'>
                                            <div className='divergence-quantity'>
                                                <div className='circle-btn' onClick={() => handleQuantityClick(quantity - 1)}><Minus size={20} /></div>
                                                <div>{quantity}</div>
                                                <div className='circle-btn' onClick={() => handleQuantityClick(quantity + 1)}><Plus size={20} /></div>
                                            </div>
                                        </div>
                                    )}

                                    <div className='divergence-actions'>
                                        <button
                                            className={`cta gold-fill ${!selectedTopFinish ? 'disabled' : ''}`}
                                            onClick={() => addToCart()}
                                            disabled={!selectedTopFinish}
                                        >
                                            Add to cart
                                        </button>
                                    </div>

                                    <div className='socials'>
                                        <a href='https://www.instagram.com/paramountwoodco/' rel="noreferrer" target='_blank'><img src={instagram} alt='Paramount Wood Co. Instagram' /></a>
                                        <a href='https://www.facebook.com/ParamountWoodCo/' rel="noreferrer" target='_blank'><img src={facebook} alt='Paramount Wood Co. Facebook' /></a>
                                        <a href='https://www.youtube.com/@paramountwoodco' rel="noreferrer" target='_blank'><img src={youtube} alt='Paramount Wood Co. Youtube' /></a>
                                        <a href='https://pinterest.com/paramountwoodcoshop/' rel="noreferrer" target='_blank'><img src={pinterest} alt='Paramount Wood Co. Pinterest' /></a>
                                        <a href='https://www.tiktok.com/@paramountwoodco' rel="noreferrer" target='_blank'><img src={tiktok} alt='Paramount Wood Co. TikTok' /></a>
                                    </div>

                                    <div className='divergence-description'>
                                        <div className='divergence-variants'>
                                            <div className={`variant-row ${!divergence.variantSpecies ? 'disabled' : ''}`}>
                                                <div className="step-description">
                                                    <h2><span>1</span> Wood Species</h2>
                                                    <p>Please select your preferred Wood Species using the drop down.</p>
                                                </div>
                                                <select
                                                    onChange={(e) => handleSpeciesChange(e.target.value)}
                                                    value={selectedSpecies?.id || ""}
                                                    className="species-select"
                                                >
                                                    <option value="">Select Material</option>
                                                    {divergence?.variantSpecies?.map((species) => (
                                                        <option key={species?.id} value={species?.id}>
                                                            {species?.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className={`variant-row ${!selectedSpecies ? 'disabled' : ''}`}>
                                                <div className="step-description">
                                                    <div className='step-title'><h2><span>2</span> Size</h2><div className='text-link' onClick={(e) => setShowSeating(true)}>Seating Chart</div></div>
                                                    <p>Please select the dimensions of your table using the drop down. <Tooltip position='top' hideTriangle={false} text={'All table tops are 1.5" thick'}><img width={20} height={20} src={infoIcon} alt='info' /></Tooltip></p> 
                                                </div>
                                                <select
                                                    onChange={(e) => handleSizeChange(e.target.value)}
                                                    value={selectedSize?.id || ""}
                                                    className="size-select"
                                                    disabled={!selectedSpecies}
                                                >
                                                    <option value="">Select Size</option>
                                                    {selectedSpecies?.metadata?.map((size) => (
                                                        <option key={size.id} value={size.id}>
                                                            {size.size}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className={`variant-row ${!selectedSize ? 'disabled' : ''}`}>
                                                <div className="step-description">
                                                    <h2><span>3</span> Live Edge</h2>
                                                    <p>Please select wether you would like a Live Edge finish. <Tooltip text="This is when the natural edge of the wood slab is kept visible and uncut. Showcasing an organic shape, grain, and character. Not shown in website images."><img width={20} height={20} src={infoIcon} alt='info' /></Tooltip></p>
                                                </div>
                                                <div className="radio-group">
                                                    <label className={`radio-label ${!selectedSize ? 'disabled' : ''}`}>
                                                        <input
                                                            type="radio"
                                                            name="liveEdge"
                                                            value="yes"
                                                            checked={selectedLiveEdge === 'yes'}
                                                            onChange={(e) => handleLiveEdgeChange(e.target.value)}
                                                            disabled={!selectedSize}
                                                        />
                                                        <span>Yes {divergence.divergenceMetadata?.liveEdgePrice > 0 && `(+${formatPrice(divergence.divergenceMetadata.liveEdgePrice)})`}</span>
                                                    </label>
                                                    <label className={`radio-label ${!selectedSize ? 'disabled' : ''}`}>
                                                        <input
                                                            type="radio"
                                                            name="liveEdge"
                                                            value="no"
                                                            checked={selectedLiveEdge === 'no'}
                                                            onChange={(e) => handleLiveEdgeChange(e.target.value)}
                                                            disabled={!selectedSize}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>

                                            {/* <div className={`variant-row ${!selectedLiveEdge ? 'disabled' : ''}`}>
                                                <div className="step-description">
                                                    <h2><span>4</span> Base Type</h2>
                                                    <p>We currently only offer the Sculpted T base type for Divergence.</p>
                                                </div>
                                                <select
                                                    className="base-type-select"
                                                    disabled
                                                >
                                                    <option value="">Sculpted T Base (Only Option)</option>
                                                </select>
                                            </div> */}

                                            <div className={`variant-row base-color ${!selectedLiveEdge ? 'disabled' : ''}`}>
                                                <div className="step-description">
                                                    <div className='step-title'><h2><span>4</span> Base Color</h2><div className='text-link' onClick={(e) => {
                                                        setShowFinishes(true);
                                                    }} rel="noreferrer">Preview finishes</div></div>

                                                    <p> Select your preferred
                                                        <span className='text-link inline' onClick={(e) => {
                                                            window.open("/bases/24?title=Sculpted%20T", "_blank")
                                                        }} rel="noreferrer">Sculpted T Base</span> color from the options below.
                                                    </p>
                                                </div>
                                                <div className='base-finish-options'>
                                                    {finishesData?.map((option, i) => (
                                                        <Tooltip key={i} position='bottom' hideTriangle={true} text={option?.name}>
                                                            <div
                                                                className={`option ${option?.name === finishOption ? "active" : ""} ${!selectedLiveEdge ? 'disabled' : ''}`}
                                                                onClick={() => selectedLiveEdge && handleBaseFinishClick(option?.name)}
                                                            >
                                                                {option?.imageFileName ? (
                                                                    <img src={`${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/finishes/${option.imageFileName}&width=50&height=50&quality=99&format=webp`} alt={option?.name} />
                                                                ) : (
                                                                    <div className='no-image'></div>
                                                                )}
                                                            </div>
                                                        </Tooltip>
                                                    ))}

                                                </div>

                                            </div>

                                            <div className={`variant-row ${!selectedBaseFinish ? 'disabled' : ''}`}>
                                                <div className="step-description">
                                                    <h2><span>5</span> Table Top Finish</h2>
                                                    <p>Please select the table top finish of your table using the options below. <Tooltip text="Our exclusive 3-step Diamond Ceramic Coating provides unmatched protection against scratches, spills, UV damage, and daily wear. Keeping wood surfaces vibrant and resilient for up to 5 years."><img width={20} height={20} src={infoIcon} alt='info' /></Tooltip></p>
                                                </div>
                                                <div className="radio-group">
                                                    <label className={`radio-label ${!selectedBaseFinish ? 'disabled' : ''}`}>
                                                        <input
                                                            type="radio"
                                                            name="topFinish"
                                                            value="Natural Oil"
                                                            checked={selectedTopFinish === 'Natural Oil'}
                                                            onChange={(e) => handleTopFinishChange(e.target.value)}
                                                            disabled={!selectedBaseFinish}
                                                        />
                                                        <span>Natural Oil</span>
                                                    </label>
                                                    <label className={`radio-label ${!selectedBaseFinish ? 'disabled' : ''}`}>
                                                        <input
                                                            type="radio"
                                                            name="topFinish"
                                                            value="Ceramic Coating"
                                                            checked={selectedTopFinish === 'Ceramic Coating'}
                                                            onChange={(e) => handleTopFinishChange(e.target.value)}
                                                            disabled={!selectedBaseFinish}
                                                        />
                                                        <span>Ceramic Coating {divergence.divergenceMetadata?.ceramicCoatingPrice > 0 && `(+${formatPrice(divergence.divergenceMetadata.ceramicCoatingPrice)})`}</span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className={`variant-row ${!selectedTopFinish ? 'disabled' : ''}`}>
                                                <div className='step-description'>
                                                    <h2><span>6</span> Ready to Order!</h2>

                                                    {selectedTopFinish &&
                                                        <div className='price-wrapper'>
                                                            <p className='divergence-price'>{formatPrice(totalPrice)} (Sculpted T Base included)</p>
                                                        </div>
                                                    }

                                                    <button
                                                        className={`cta gold-fill ${!selectedTopFinish ? 'disabled' : ''}`}
                                                        onClick={() => addToCart()}
                                                        disabled={!selectedTopFinish}
                                                    >
                                                        Add to cart
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {divergence.divergenceMetadata?.specification &&
                                            <>
                                                <h3>Specification</h3>
                                                <div className='html-text' dangerouslySetInnerHTML={{ __html: divergence.divergenceMetadata.specification }} />
                                            </>
                                        }

                                        {divergence.divergenceMetadata?.description &&
                                            <>
                                                <h3>Description</h3>
                                                <div className='html-text' dangerouslySetInnerHTML={{ __html: divergence.divergenceMetadata.description }} />
                                            </>
                                        }
                                    </div>
                                </div>
                            </div>
                        </Animate>

                        {recommendedProducts?.length > 0 &&
                            <div className='divergence-other'>
                                <h2>You may also like</h2>
                                <div className='items'>
                                    {recommendedProducts.map((item, i) => (
                                        <Item key={i} item={item} index={i} setItem={onProductClick} type={"products"} />
                                    ))}
                                </div>
                            </div>
                        }
                    </div>

                    {showReadMore &&
                        <FeedbackModal status={""} type={"info"} title={"Specialized Shipping"} message={"Some items are too large and heavy for conventional shipping methods. After your purchase, we will contact you to arrange an alternative at a competitive price."} onClose={onFeedbackClose} label={"Close"} />
                    }
                </>
            }

            {showSeating &&
                <div className='overlay' onClick={(e) => {
                    e.stopPropagation();
                    setShowSeating(false)
                }}>
                    <div className='overlay-content' onClick={(e) => {
                        e.stopPropagation();
                    }}>
                        <img alt='seating plan' src={process.env.PUBLIC_URL + '/images/hosted/seating_plan.jpg'} />
                        <button className='cta' onClick={() => setShowSeating(false)}>Close</button>
                    </div>
                </div>
            }

            {showFinishes &&
                <div className='overlay' onClick={(e) => {
                    e.stopPropagation()
                    setShowFinishes(false)
                }}>
                    <div className='overlay-content' onClick={(e) => {
                        e.stopPropagation()
                    }}>
                        <div className='finish-modal'>
                            {finishesData?.map((option, i) => (
                                <div key={i} className='finish'>
                                    <img src={`${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/finishes/${option?.imageFileName}&width=200&height=200&quality=99&format=webp`} alt={option?.name} />
                                    <p>{option?.name}</p>
                                </div>
                            ))}

                        </div>
                        <button className='cta' onClick={() => setShowFinishes(false)}>Close</button>
                    </div>

                </div>

            }
        </>
    );
};

export default Divergence;